<template>
  <div>
    <ValidationObserver ref="form">
      <section
        :key="typeof page.title === 'function' ? page.title() : page.title"
        v-for="(page, i) in pages"
        :class="{
          'editing-addresss': editingAddress && page.usePostCodeLookup,
        }"
      >
        <template v-if="currentPage === i">
          <div class="page-intro">
            <HeaderWithIcon
              :title="
                typeof page.title === 'function' ? page.title() : page.title
              "
            />
            <div v-if="page.text !== ''"><span v-html="page.text" /></div>
          </div>
          <postcode-search
            @disableNext="disableNextButton"
            @place-change="mapAddress"
            v-if="page.usePostCodeLookup"
          />

          <div v-if="page.usePostCodeLookup">
            <section v-if="addressSelected && !editingAddress">
              <div class="selected-address">
                <p>Your Address</p>
                <ul>
                  <li>{{ page.fields[0].value }}</li>
                  <li>{{ page.fields[1].value }}</li>
                  <li>{{ page.fields[2].value }}</li>
                  <li>{{ page.fields[3].value }}</li>
                  <li>
                    {{ countries[page.fields[4].value] }}
                  </li>
                </ul>
              </div>
            </section>
            <section style="margin-bottom: 1em; text-align: right;">
              <button
                @click="handleAddressEdit"
                class="btn btn--link"
                v-if="!editingAddress"
              >
                <span v-if="!addressSelected">Enter manually</span>
                <span v-if="addressSelected">Edit address</span>
              </button>
              <button
                @click="handleAddressEdit"
                class="btn btn--link"
                v-if="editingAddress"
              >
                <span>Cancel</span>
              </button>
            </section>
          </div>

          <form-component :page="page" />

          <p v-if="showLogin">
            It appears you may have already started registration.
            <a :href="loginUrl">Login to continue</a>
          </p>
          <notification />
        </template>
      </section>

      <section class="form-controls">
        <button
          v-if="showPrevBtn && !isFromReview"
          role="button"
          class="btn btn--link"
          @click="decrementPage"
        >
          Back
        </button>
        <button
          role="button"
          class="btn btn--positive"
          @click="incrementPage"
          :disabled="!allowNext"
        >
          {{ isFromReview ? "Confirm" : "Next" }}
        </button>
      </section>
    </ValidationObserver>
  </div>
</template>

<script>
import FormComponent from "@/components/FormComponent";
import HeaderWithIcon from "@/components/HeaderWithIcon";
import Notification from "@/components/Notification";
import PostcodeSearch from "@/components/PostcodeSearch";
import { ContactDetailsDefinition } from "@/definitions/section-definitions.js";
import { registrationConfig } from "@/helpers/config";
import { useLoggedInStatus } from "@/helpers/loggedInStatus";
import { useMemberDetails } from "@/helpers/member-details";
import { useNotification } from "@/helpers/notification";
import { filterPages, goToPreviousPage } from "@/helpers/page";
import { nextStep } from "@/helpers/progress-helper";
import { useShowSpinner } from "@/helpers/showSpinner";
import { computed, onMounted, ref, watch } from "@vue/composition-api";
import { ValidationObserver } from "vee-validate";

export default {
  components: {
    FormComponent,
    ValidationObserver,
    PostcodeSearch,
    Notification,
    HeaderWithIcon,
  },
  props: {
    currentPage: { default: 0, type: Number },
  },
  setup(props, ctx) {
    const { isLoggedIn } = useLoggedInStatus(),
      { setShowSpinner } = useShowSpinner(),
      memberDetails = useMemberDetails(),
      showLogin = ref(false),
      form = ref(null),
      addressSelected = ref(false),
      editingAddress = ref(false),
      loginUrl = `/auth/requestsignin?returnurl=${window.location.href}`;
    const { overseasMembershipCodes, countries } = registrationConfig;

    const { showNotification } = useNotification();

    const pages = ref(filterPages(ContactDetailsDefinition.pages, isLoggedIn));

    const allowNext = ref(true);

    const isFromReview =
      ctx.root.$router.currentRoute.query.fromReview !== undefined;

    const isInternationalMember = overseasMembershipCodes.includes(
      memberDetails.membershipStatusType.value,
    );

    if (isFromReview) {
      addressSelected.value = true;
    }

    async function incrementPage() {
      const success = await form.value.validate();
      if (!success) return;

      const page = pages.value[props.currentPage];
      if (!page.completionAction) {
        return nextStep(ctx.root.$router);
      }

      setShowSpinner(true);
      const result = await page.completionAction(page.fields);
      const { existing, lapsed, isVerified, userName } = useMemberDetails();

      if (result.success) {
        let userNameEmail = page.fields.filter((item) => {
          return item.key === "email";
        });

        if (userNameEmail.length > 0) userName.value = userNameEmail[0].value;
      }

      if (result.linked && !lapsed.value) {
        showNotification("success", "Success linking your account");

        if (!isVerified.value) {
          ctx.root.$router.push("/register/membership/verify-account");
        } else {
          return (window.location.pathname = "/profile");
        }
      }

      setShowSpinner(false);

      if (!result.success) {
        showNotification("error", result.message, result.persistent);
        return;
      }

      if (result.message)
        showNotification("success", result.message, result.persistent);

      if (result.linked && existing.value) {
        if (!isVerified.value) {
          ctx.root.$router.push("/register/membership/verify-account");
        } else {
          return (window.location.pathname = "/profile");
        }
      }

      memberDetails.isMember.value = true;

      nextStep(ctx.root.$router);
    }

    function mapAnswersToCurrentPage() {
      let thisPage = pages.value[props.currentPage];
      if (thisPage) {
        thisPage.fields.forEach((field) => {
          Object.keys(memberDetails).map(function(key) {
            if (key === field.key) {
              field.value = memberDetails[key].value;
            }
          });
        });
      }
    }

    const handleAddressEdit = () =>
      (editingAddress.value = !editingAddress.value);

    onMounted(() => {
      if (
        !memberDetails.membershipType.value &&
        !memberDetails.membershipNumber.value
      ) {
        ctx.root.$router.push({ name: "NewMember" });
      }
    });

    watch(
      () => props.currentPage,
      () => {
        mapAnswersToCurrentPage();
      },
    );

    mapAnswersToCurrentPage();

    return {
      allowNext,
      setShowSpinner,
      isLoggedIn,
      pages,
      isInternationalMember,
      showPrevBtn: computed(() => {
        if (
          ctx.root.$router.currentRoute.name === "Contact Details" &&
          !memberDetails.isVerified.value &&
          props.currentPage >= 0
        )
          return true;
        else return memberDetails.isVerified.value && props.currentPage >= 1;
      }),
      skipPage: () => props.currentPage++,
      decrementPage: () => {
        allowNext.value = true;
        goToPreviousPage(ctx.root.$router);
      },
      showLogin,
      incrementPage,
      form,
      editingAddress,
      loginUrl,
      isFromReview,
      handleAddressEdit,
      addressSelected,
      countries,
    };
  },
  methods: {
    disableNextButton: function() {
      let currentPageSet = this.pages[this.currentPage];
      if (currentPageSet.usePostCodeLookup) {
        if (
          (currentPageSet && currentPageSet.fields[0].value !== "") ||
          currentPageSet.fields[1].value !== "" ||
          currentPageSet.fields[2].value !== "" ||
          currentPageSet.fields[3].value !== "" ||
          currentPageSet.fields[4].value !== ""
        ) {
          this.allowNext = true;
        } else {
          this.allowNext = false;
        }
      }
    },
    mapAddress: function(address) {
      this.allowNext = true;
      this.addressSelected = true;
      let currentPageSet = this.pages[this.currentPage];
      currentPageSet.fields[0].value = "";
      currentPageSet.fields[1].value = "";
      currentPageSet.fields[2].value = "";
      currentPageSet.fields[3].value = "";
      if (currentPageSet) {
        currentPageSet.fields[0].value = `${address.Line1}, ${address.Line2}`;
        currentPageSet.fields[1].value = `${address.City}`;
        currentPageSet.fields[2].value = `${
          address.Province !== "" ? address.Province : address.AdminAreaName
        }`;
        currentPageSet.fields[3].value = `${address.PostalCode}`;
        currentPageSet.fields[4].value = `${address.CountryIso3}`;
      }
    },
  },
};
</script>
<style lang="scss">
.selected-address {
  background: #f5f5f5;
  padding: 1em;
  margin-bottom: 1em;

  p {
    font-weight: 600;
    margin: 0 0 1em;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;

    li + li {
      margin-top: 0.5em;
    }
  }
}
</style>
